import { Loader2, RotateCcw } from "lucide-react";

import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";

import type {
	LineSyncStatus,
	RestoreSyncStatus,
} from "../epos-context/order-write-queue";

/**
 * HA-768 / D-HA-24 — what a line shows while the server hasn't confirmed it. Nothing shows as done
 * before the server has it (24-1): a new or edited line is Sending, a removed one Removing; on a
 * failure it is Not saved or Not removed, with a Retry; offline it is Not yet sent (the carve-out).
 * A line with no outstanding write shows nothing.
 */
const LABELS: Record<LineSyncStatus | RestoreSyncStatus, string> = {
	restoring: "Restoring…",
	"not-restored": "Not restored",
	sending: "Sending…",
	removing: "Removing…",
	"not-saved": "Not saved",
	"not-removed": "Not removed",
	"not-yet-sent": "Not yet sent",
};

export function LineSyncBadge({
	status,
	onRetry,
}: {
	status: LineSyncStatus | RestoreSyncStatus | null;
	onRetry?: () => void;
}) {
	if (!status) return null;

	const failed =
		status === "not-saved" ||
		status === "not-removed" ||
		status === "not-restored";
	const busy =
		status === "sending" || status === "removing" || status === "restoring";

	return (
		<div className="flex items-center gap-1 mx-1" data-line-sync={status}>
			<Badge
				variant="outline"
				className={
					failed
						? "border-destructive text-destructive"
						: status === "not-yet-sent"
							? "border-amber-400 text-amber-700"
							: "text-muted-foreground"
				}
			>
				{busy && <Loader2 className="size-3 animate-spin" />}
				{LABELS[status]}
			</Badge>
			{failed && onRetry && (
				<Button variant="outline" size="sm" onClick={onRetry}>
					<RotateCcw className="size-3" />
					Retry
				</Button>
			)}
		</div>
	);
}
